import type { Block, Cell, ChipCell, Column, Tone } from '@/kova/spec/types'

import type { ApiClient } from '@/lib/api'
import type { ScreenData, ScreenLoader } from './provenance'
import {
  asArray, asRecord, attempt, disclosure, knows, malformed, readBool, readInstant,
  readInt, readText, receipt, refLine, summarise,
  type Attempt,
} from './wire'

/**
 * Content › Payout guidance (`ct-payout`), read from
 * `GET /v1/admin/payout-guidance`, with one platform-and-language's history
 * opening from a row via `GET /payout-guidance/{platform_id}/{lang}/versions`.
 *
 * # What this screen is for
 *
 * It is the wording a creator is shown when they are told how to get paid from
 * a platform — the field to fill in, the steps, and the mistake people make.
 * Wrong here, a customer sends money to the wrong place, so the screen's job is
 * to make it obvious which wording is LIVE and which is merely the newest.
 *
 * # A platform with no published guidance is ABSENT, not empty
 *
 * The live list carries the platforms that have guidance. One that has none
 * does not appear at all, so the row count is not a count of platforms — and a
 * screen implying otherwise would report full coverage from a partial list.
 * The screen says so rather than letting the count be read as coverage.
 *
 * # `live` and `review_state` are two facts and neither implies the other
 *
 * A version can be approved and not yet live, and a live version is not
 * necessarily the newest. Both are drawn, because the question an operator
 * brings here is "what is the customer reading right now", and only `live`
 * answers it.
 *
 * # No screenshots, and there is no field for one
 *
 * The design draws a screenshot of the platform's payout page. Nothing stores
 * one and the response has no field for it — dropped rather than left as a
 * broken frame.
 *
 * # Read-only, and law 6 is why the control is not inherited
 *
 * There is no create, edit, publish or retire on this surface. Guidance is
 * VERSIONED — a change is a new version with its own review state and its own
 * effective interval, never an edit of the wording a customer already followed
 * — so the control law 6 requires is a "save as new version" form with a diff
 * against live and an effective date. That is a shape to be argued in a change
 * that designs it, not one to inherit from a read.
 */

export const PAYOUT_GUIDANCE_PATH = '/payout-guidance'

export function payoutGuidanceVersionsPath(platformID: string, lang: string): string {
  return `${PAYOUT_GUIDANCE_PATH}/${encodeURIComponent(platformID)}/${encodeURIComponent(lang)}/versions`
}

const SOURCE = 'postgres · payout_guidance (GET /v1/admin/payout-guidance)'
const AUTHORITY = 'operational — our own wording, not the platform’s'

/**
 * Review states.
 *
 * `approved` is neutral rather than green: approved is not live, and green on
 * it is exactly the misreading this screen exists to prevent. Green is reserved
 * for the row a customer is actually reading.
 */
const REVIEW_TONE: Readonly<Record<string, Tone>> = {
  draft: 'n',
  in_review: 'a',
  approved: 'n',
  rejected: 'a',
  superseded: 'n',
}

const COLS: readonly Column[] = [
  ['Platform', 'm', '150px'],
  ['Language', 'm', '104px'],
  ['Version', 'n', '92px'],
  ['Customer is reading this', 'c', '176px'],
  ['Review state', 'c', '140px'],
  ['Field they must fill in', 'x', 'minmax(180px,1.3fr)'],
  ['Steps', 'n', '90px'],
  ['Common mistake', 'x', 'minmax(190px,1.4fr)'],
  ['Effective from', 'm', '150px'],
  ['Authored by', 'x', '140px'],
]

interface RowRead {
  readonly cells: readonly Cell[]
  readonly gaps: readonly string[]
  /** `platform_id/lang`, which is what the versions read is keyed by. */
  readonly key: string | null
  readonly live: boolean
}

function readGuidance(raw: unknown): RowRead | null {
  const o = asRecord(raw)
  if (o === null) return null
  const gaps: string[] = []

  const platform = readText(o, 'platform_id')
  if (platform === null) gaps.push('a platform')
  const lang = readText(o, 'lang')
  if (lang === null) gaps.push('a language')

  const version = readInt(o, 'version')
  if (version.kind !== 'exact') gaps.push('a version')

  // The question the screen exists to answer. Green here and nowhere else.
  const live = readBool(o, 'live')
  if (live === null) gaps.push('whether a version is the one customers read')
  const liveCell: ChipCell = live === null
    ? ['unstated', 'a']
    : live ? ['yes — this is live', 'g'] : ['no', 'n']

  // A separate fact. Approved is not live, and the tone says so.
  const review = readText(o, 'review_state')
  if (review === null) gaps.push('a review state')
  const knownReview = review !== null && knows(REVIEW_TONE, review)
  if (review !== null && !knownReview) gaps.push('a review state this console recognises')
  const reviewCell: ChipCell = [review ?? 'unstated', knownReview ? (REVIEW_TONE[review] ?? 'a') : 'a']

  const field = readText(o, 'field_label')
  if (field === null) gaps.push('the field a customer must fill in')

  const steps = asArray(o['steps'])
  if (steps === null) gaps.push('the steps a customer follows')

  const from = readInstant(o, 'effective_from')

  return {
    cells: [
      platform ?? '— unnamed',
      lang ?? '—',
      version.kind === 'exact' ? String(version.value) : '—',
      liveCell,
      reviewCell,
      field ?? '— not stated',
      steps === null ? '—' : String(steps.length),
      // `omitempty`: guidance with no known pitfall carries none, which is a
      // position rather than a gap.
      readText(o, 'common_pitfall') ?? '— none recorded',
      from.kind === 'at' ? from.label : from.kind === 'absent' ? '— not dated' : '—',
      readText(o, 'authored_by') ?? '— unattributed',
    ],
    gaps,
    key: platform === null || lang === null ? null : `${platform}/${lang}`,
    live: live === true,
  }
}

const MISSING: readonly string[] = [
  'a screenshot of the platform’s payout page — nothing stores one and this response has no field for it',
  'the platforms with no guidance at all — a platform without it is absent from this read, so the row count is a count of published guidance rather than of platforms',
  'an authoring control — guidance is versioned, so a change is a new version with its own review and effective interval; that is law 6’s save-as-new-version form and it belongs in a change that designs it',
]

export function composePayoutGuidance(read: Attempt, at: Date): ScreenData {
  if (!read.ok) {
    return {
      blocks: [{
        t: 'note', tone: 'r', kicker: 'Payout guidance',
        title: 'The guidance could not be read',
        body: `${read.reason} No wording below is known — that is different from no guidance being published.`,
        ref: refLine(read, 'Nothing on this screen was read.'),
      }],
      provenance: { kind: 'unread', source: SOURCE, reason: read.reason },
    }
  }

  const o = asRecord(read.payload)
  const list = o === null ? null : asArray(o['rows'])
  if (o === null || list === null) {
    const bad = malformed('The guidance returned a shape this console does not know.')
    return {
      blocks: [{
        t: 'note', tone: 'r', kicker: 'Payout guidance',
        title: 'The guidance could not be read', body: bad.reason,
        ref: 'Nothing on this screen was read.',
      }],
      provenance: { kind: 'unread', source: SOURCE, reason: bad.reason },
    }
  }

  const gaps: string[] = []
  const rows: (readonly Cell[])[] = []
  const rowKeys: (string | null)[] = []
  let unreadable = 0
  let live = 0
  const platforms = new Set<string>()

  for (const raw of list) {
    const row = readGuidance(raw)
    if (row === null) { unreadable += 1; continue }
    rows.push(row.cells)
    rowKeys.push(row.key)
    gaps.push(...row.gaps)
    if (row.live) live += 1
    const p = asRecord(raw)
    const id = p === null ? null : readText(p, 'platform_id')
    if (id !== null) platforms.add(id)
  }

  // The row count is a count of PUBLISHED GUIDANCE, never of platforms — a
  // platform with none is absent from this read entirely.
  const footer = rows.length === 0
    ? (unreadable === 0
        ? 'No payout guidance is published. A platform with none is absent from this read, so an empty list means nothing has been written rather than that no platform needs it.'
        : `No guidance could be read: ${String(unreadable)} came back in a shape this console does not know.`)
    : `${String(rows.length)} published ${rows.length === 1 ? 'version' : 'versions'} across ${String(platforms.size)} ${platforms.size === 1 ? 'platform' : 'platforms'} · ${String(live)} live · a platform with no guidance is absent rather than listed empty`

  const blocks: Block[] = [{
    t: 'table', title: 'Payout guidance', src: 'ledger', facets: false,
    cols: COLS, rows, footer,
  }]

  const coverage = disclosure(o, 'coverage_note', 'Coverage', 'What this list does and does not contain', 'n')
  if (coverage !== null) blocks.push(coverage)
  const shot = disclosure(o, 'screenshot_note', 'Screenshots', 'Why there is no image here', 'n')
  if (shot !== null && rows.length > 0) blocks.push(shot)

  const missing = [...MISSING, ...summarise(gaps)]
  if (unreadable > 0) missing.push(`${String(unreadable)} guidance row(s) this console could not read`)

  return {
    blocks,
    provenance: { kind: 'partial', source: SOURCE, authority: AUTHORITY, readAt: receipt(at), missing },
    title: 'Payout guidance',
    subject: `payout_guidance · ${String(rows.length)} ${rows.length === 1 ? 'row' : 'rows'}`,
    rowKeys,
  }
}

export const loadPayoutGuidance: ScreenLoader = async (api) =>
  composePayoutGuidance(await attempt(() => api.get<unknown>(PAYOUT_GUIDANCE_PATH), 'The payout guidance'), new Date())

// ---------------------------------------------------------------------------
// One platform and language's whole history — the row opener
// ---------------------------------------------------------------------------

const HISTORY_COLS: readonly Column[] = [
  ['Version', 'n', '92px'],
  ['Customer is reading this', 'c', '176px'],
  ['Review state', 'c', '140px'],
  ['Field they must fill in', 'x', 'minmax(180px,1.3fr)'],
  ['Effective', 'm', 'minmax(190px,1.3fr)'],
  ['Authored by', 'x', '140px'],
  ['Reviewed by', 'x', '140px'],
]

export function composeGuidanceHistory(read: Attempt, key: string, at: Date): ScreenData {
  if (!read.ok) {
    return {
      blocks: [{
        t: 'note', tone: 'r', kicker: 'Payout guidance',
        title: 'This history could not be read',
        body: `${read.reason} Nothing about it is known — including whether any version was ever published.`,
        ref: refLine(read, 'Nothing on this screen was read.'),
      }],
      provenance: { kind: 'unread', source: SOURCE, reason: read.reason },
    }
  }

  const o = asRecord(read.payload)
  const list = o === null ? null : asArray(o['versions'])
  if (o === null || list === null) {
    const bad = malformed('The history returned a shape this console does not know.')
    return {
      blocks: [{
        t: 'note', tone: 'r', kicker: 'Payout guidance',
        title: 'This history could not be read', body: bad.reason,
        ref: 'Nothing on this screen was read.',
      }],
      provenance: { kind: 'unread', source: SOURCE, reason: bad.reason },
    }
  }

  const gaps: string[] = []
  const rows: (readonly Cell[])[] = []
  let unreadable = 0

  for (const raw of list) {
    const v = asRecord(raw)
    if (v === null) { unreadable += 1; continue }

    const version = readInt(v, 'version')
    const live = readBool(v, 'live')
    const review = readText(v, 'review_state')
    const knownReview = review !== null && knows(REVIEW_TONE, review)
    const from = readInstant(v, 'effective_from')
    const to = readInstant(v, 'effective_to')
    if (version.kind !== 'exact') gaps.push('a version number')

    rows.push([
      version.kind === 'exact' ? String(version.value) : '—',
      live === null ? ['unstated', 'a'] : live ? ['yes — this is live', 'g'] : ['no', 'n'],
      [review ?? 'unstated', knownReview ? (REVIEW_TONE[review] ?? 'a') : 'a'],
      readText(v, 'field_label') ?? '— not stated',
      // An open interval is the live one; a closed one is history. Rendered as
      // the interval rather than as a single date, because "from" alone reads
      // as current on every row.
      from.kind === 'at'
        ? (to.kind === 'at' ? `${from.label} → ${to.label}` : `${from.label} → open`)
        : '—',
      readText(v, 'authored_by') ?? '— unattributed',
      // `omitempty`: an unreviewed version has nobody, which is a position.
      readText(v, 'reviewed_by') ?? '— not reviewed',
    ])
  }

  const blocks: Block[] = [{
    t: 'table', title: `Every version · ${key}`, src: 'ledger', facets: false,
    cols: HISTORY_COLS, rows,
    footer: rows.length === 0
      ? (unreadable === 0
          ? 'No version has been published for this platform and language.'
          : `No version could be read: ${String(unreadable)} came back in a shape this console does not know.`)
      : `${String(rows.length)} ${rows.length === 1 ? 'version' : 'versions'} · oldest first, and a superseded version stays because a customer followed it`,
  }]

  const history = disclosure(o, 'history_note', 'History', 'Where this history begins', 'n')
  if (history !== null && rows.length > 0) blocks.push(history)

  const missing = [
    'an authoring or publish control — a change here is a new version, which is law 6’s save-as-new-version form and belongs in a change that designs it',
    ...summarise(gaps),
  ]
  if (unreadable > 0) missing.push(`${String(unreadable)} version(s) this console could not read`)

  return {
    blocks,
    provenance: { kind: 'partial', source: SOURCE, authority: AUTHORITY, readAt: receipt(at), missing },
    title: `Payout guidance · ${key}`,
    subject: `payout_guidance · ${String(rows.length)} ${rows.length === 1 ? 'row' : 'rows'}`,
  }
}

/**
 * One platform and language's history, opened from a row.
 *
 * The key is `platform_id/lang` because the read is keyed by both — a language
 * is not a property of the platform's guidance, it IS half of its identity, and
 * splitting on the first slash would break a platform id that contains one.
 */
export async function readGuidanceHistory(api: ApiClient, key: string): Promise<ScreenData> {
  const cut = key.lastIndexOf('/')
  if (cut <= 0) {
    return composeGuidanceHistory(
      malformed(`"${key}" does not name a platform and a language.`), key, new Date(),
    )
  }
  const platform = key.slice(0, cut)
  const lang = key.slice(cut + 1)
  return composeGuidanceHistory(
    await attempt(() => api.get<unknown>(payoutGuidanceVersionsPath(platform, lang)),
      `The guidance history for ${key}`),
    key, new Date(),
  )
}
